import React from 'react';
import { Icon } from '@repo/ui';
import { Button, Input } from '@repo/ui';
import type { EmailForm } from '../types';

// Standardized button styles
const BUTTON_STYLES = {
  standard: {
    variant: 'outline' as const,
    size: 'sm' as const,
  },
  primary: {
    variant: 'primary' as const,
    size: 'md' as const,
  },
} as const;

const BUTTON_CLASSES = {
  group: 'gap-2',
} as const;

interface EmailSenderFormProps {
  emailForm: EmailForm;
  onEmailFormChange: (form: EmailForm) => void;
  onSend: () => void;
  onCancel: () => void;
  isSending: boolean;
}

export const EmailSenderForm: React.FC<EmailSenderFormProps> = ({
  emailForm,
  onEmailFormChange,
  onSend,
  onCancel,
  isSending,
}) => {
  const handleAttachmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    onEmailFormChange({ ...emailForm, attachment: file || null });
  };

  return (
    <div className="bg-gray-50 p-4 rounded-lg border space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Input
            type="email"
            label="To Email Address *"
            autoComplete="email"
            value={emailForm.toEmail}
            onChange={e => onEmailFormChange({ ...emailForm, toEmail: e.target.value })}
            placeholder="recipient@example.com"
            required
            fullWidth
          />
        </div>

        <div>
          <Input
            label="Subject Line *"
            value={emailForm.subject}
            onChange={e => onEmailFormChange({ ...emailForm, subject: e.target.value })}
            placeholder="Test Email - Invoice Notification"
            required
            fullWidth
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Attachment (Optional)
        </label>
        <div className="flex items-center space-x-4">
          <label className="inline-flex items-center px-4 py-2 bg-xtnd-blue text-white rounded-lg hover:bg-xtnd-dark transition-colors cursor-pointer">
            <Icon name="HiDocumentAdd" className="h-4 w-4 mr-2" />
            Choose File
            <Input
              type="file"
              className="hidden"
              onChange={handleAttachmentChange}
              accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
            />
          </label>
          {emailForm.attachment && (
            <div className="flex items-center text-sm text-gray-600">
              <span>{emailForm.attachment.name}</span>
              <Button
                {...BUTTON_STYLES.standard}
                onClick={() => onEmailFormChange({ ...emailForm, attachment: null })}
                className="ml-2 p-1"
                leftIcon={<Icon name="HiTrash" className="h-3 w-3" />}
              >
                Remove
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Input
          type="checkbox"
          id="secureAttachment"
          checked={emailForm.secureAttachment}
          onChange={e =>
            onEmailFormChange({
              ...emailForm,
              secureAttachment: e.target.checked,
            })
          }
          className="rounded border-gray-300"
        />
        <label htmlFor="secureAttachment" className="text-sm text-gray-700">
          Use secure attachment (password-protected)
        </label>
      </div>

      <div className={`flex justify-end ${BUTTON_CLASSES.group}`}>
        <Button {...BUTTON_STYLES.standard} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={onSend}
          disabled={isSending || !emailForm.toEmail || !emailForm.subject}
          loading={isSending}
          {...BUTTON_STYLES.primary}
          leftIcon={<Icon name="HiMail" className="h-4 w-4" />}
        >
          Send Test Email
        </Button>
      </div>
    </div>
  );
};

